import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import {Popover} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useEffect, useState} from "react";
import {BottomDrawer} from "~/components/BottomDrawer";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {
    applyJourneyScopeSelectionToFilterConfiguration,
    getJourneyScopeSelectionFromFilter,
    type JourneyScopeFilterConfig,
    type JourneyScopeSelection,
} from "~/components/ottopilotDataViewer/journeyScopeFilterConfiguration";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeCheckbox} from "@vesta/ananke/components/anankeCheckbox";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

const JOURNEY_SCOPE_NON_ALL_OPTIONS: Array<{id: Exclude<JourneyScopeSelection, "all">; label: string}> = [
    {id: "active", label: "Active"},
    {id: "inactive", label: "Inactive"},
];

export function JourneyScopeFilterComponent<T extends {id: Uuid}>({
    controller,
    journeyScopeFilterConfig,
    disabled = false,
}: {
    controller: OttopilotDataViewerController<T>;
    journeyScopeFilterConfig: JourneyScopeFilterConfig;
    disabled?: boolean;
}) {
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const [opened, setOpened] = useState(false);
    const [ephemeralSelection, setEphemeralSelection] = useState<JourneyScopeSelection>("all");

    const selection = getJourneyScopeSelectionFromFilter(controller.filterConfiguration, journeyScopeFilterConfig);
    const hasActiveJourneyScope = selection !== "all";

    useEffect(() => {
        if (disabled === true && opened === true) {
            setOpened(false);
        }
    }, [disabled, opened]);

    const openPanel = () => {
        if (disabled === true) {
            return;
        }
        setEphemeralSelection(
            getJourneyScopeSelectionFromFilter(controller.filterConfiguration, journeyScopeFilterConfig),
        );
        setOpened(true);
    };

    const closePanel = () => {
        setOpened(false);
    };

    const discardAndClose = () => {
        setOpened(false);
    };

    const applyAndClose = () => {
        controller.setFilterConfiguration((prev) =>
            applyJourneyScopeSelectionToFilterConfiguration(prev, journeyScopeFilterConfig, ephemeralSelection),
        );
        setOpened(false);
    };

    const allChecked = ephemeralSelection === "all";

    const setAllSelected = () => {
        setEphemeralSelection("all");
    };

    const toggleNonAllOption = (optionId: Exclude<JourneyScopeSelection, "all">) => {
        setEphemeralSelection((prev) => {
            if (prev === optionId) {
                return "all";
            }
            return optionId;
        });
    };

    const triggerButton = (
        <AnankeButton
            type="button"
            appearance="default"
            spacing="default"
            isDisabled={disabled}
            isSelected={disabled !== true && hasActiveJourneyScope}
            onClick={() => {
                if (disabled === true) {
                    return;
                }
                if (opened === false) {
                    openPanel();
                } else {
                    closePanel();
                }
            }}
        >
            <div className="grid grid-flow-col auto-cols-max gap-x-[0.375rem] items-center">
                <span className="whitespace-nowrap">Display by</span>
                <ChevronDownIcon
                    label=""
                    size="small"
                />
            </div>
        </AnankeButton>
    );

    const panelBody = (
        <>
            <div className="w-full min-w-[16rem] max-w-[20rem] md:px-ds-space-100">
                <div className="w-full pr-ds-space-200 py-ds-space-100 grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-ds-space-150">
                    <AnankeCheckbox
                        isChecked={allChecked}
                        onChange={(e) => {
                            if (e.target.checked === true) {
                                setAllSelected();
                            }
                        }}
                    />
                    <div>All</div>
                </div>
                <ItemBuilder
                    items={JOURNEY_SCOPE_NON_ALL_OPTIONS}
                    itemBuilder={(option) => (
                        <div
                            key={option.id}
                            className="w-full pr-ds-space-200 py-ds-space-075 grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-ds-space-150"
                        >
                            <AnankeCheckbox
                                isChecked={ephemeralSelection === option.id}
                                onChange={() => {
                                    toggleNonAllOption(option.id);
                                }}
                            />
                            <div>{option.label}</div>
                        </div>
                    )}
                />
            </div>
        </>
    );

    const panelFooter = (
        <div>
            <div className="w-full h-[var(--ds-border-width)] bg-ds-border" />
            <div className="px-ds-space-100 py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
                <AnankeButton
                    type="button"
                    appearance="subtle"
                    onClick={discardAndClose}
                >
                    Cancel
                </AnankeButton>
                <AnankeButton
                    type="button"
                    appearance="primary"
                    onClick={applyAndClose}
                >
                    Apply
                </AnankeButton>
            </div>
        </div>
    );

    if (isMobile === true) {
        return (
            <>
                {triggerButton}
                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title="Display by"
                    zIndex={100}
                    fitContentOnOpen={true}
                    footer={panelFooter}
                >
                    {panelBody}
                </BottomDrawer>
            </>
        );
    }

    return (
        <Popover
            position="bottom-start"
            width={320}
            opened={opened}
            onChange={(next) => {
                if (next === true) {
                    openPanel();
                } else {
                    closePanel();
                }
            }}
            classNames={{
                dropdown: "p-0!",
            }}
            shadow="lg"
        >
            <Popover.Target>{triggerButton}</Popover.Target>
            <Popover.Dropdown>
                {panelBody}
                {panelFooter}
            </Popover.Dropdown>
        </Popover>
    );
}
